import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

export const MissionPage = () => {
    return (
        <div className="section-gap" data-testid="mission-page">
            <div className="container-main">
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-12">
                    <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                        Our Story
                    </span>
                    <h1 className="font-serif text-4xl md:text-5xl font-semibold text-primary mt-2">
                        What is Zarolla<span className="text-[0.6em] align-super">™</span>?
                    </h1>
                </div>

                {/* The Three Masters - Now under the title */}
                <div className="max-w-4xl mx-auto mb-16">
                    <h2 className="font-serif text-2xl font-medium text-center mb-8">
                        The Usual Suspects
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card className="overflow-hidden">
                            <div className="aspect-[3/4] bg-secondary">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_aac1ed7e-c905-494c-a664-04e422519d94/artifacts/h4wzg0xu_anders-zorn-1908.jpg.webp"
                                    alt="Anders Zorn"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6 text-center">
                                <h3 className="font-serif text-lg font-medium">Anders Zorn</h3>
                                <p className="text-xs text-muted-foreground mt-1">1860–1920</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Swedish master of light and the limited palette
                                </p>
                            </div>
                        </Card>
                        <Card className="overflow-hidden">
                            <div className="aspect-[3/4] bg-secondary">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_aac1ed7e-c905-494c-a664-04e422519d94/artifacts/jf2adz1f_Sargent.jpeg"
                                    alt="John Singer Sargent"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6 text-center">
                                <h3 className="font-serif text-lg font-medium">John Singer Sargent</h3>
                                <p className="text-xs text-muted-foreground mt-1">1856–1925</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    The virtuoso of portraiture and brushwork
                                </p>
                            </div>
                        </Card>
                        <Card className="overflow-hidden">
                            <div className="aspect-[3/4] bg-secondary">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_aac1ed7e-c905-494c-a664-04e422519d94/artifacts/zn2qa4r8_Sorolla.jpeg"
                                    alt="Joaquín Sorolla"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="p-6 text-center">
                                <h3 className="font-serif text-lg font-medium">Joaquín Sorolla</h3>
                                <p className="text-xs text-muted-foreground mt-1">1863–1923</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Master of Mediterranean light and color
                                </p>
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-3xl mx-auto">
                    <Card className="p-8 md:p-12">
                        <div className="prose prose-lg max-w-none">
                            <p className="text-lg text-foreground leading-relaxed mb-6">
                                I founded Zarolla<span className="text-[0.7em] align-super">™</span> in 2007 with a clear vision: to create the first true online art school built around live filming technology — at a time when virtually no one else was doing it.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-6">
                                The idea was born during a difficult period in Los Angeles. The housing market collapse had deeply affected many of my closest friends and colleagues, particularly illustrators and working artists whose livelihoods depended on a fragile creative economy. I was still working in the entertainment industry when rapid technological shifts in Home Entertainment led to widespread layoffs, including my own.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-6">
                                Rather than view that moment as a setback, I saw an opportunity — not only to help myself pivot, but to create something that could sustain and support serious artists regardless of geography.
                            </p>

                            <p className="text-foreground font-medium leading-relaxed mb-6">
                                Zarolla became that solution.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-6">
                                Years before livestreaming became commonplace, Zarolla was delivering real-time instruction, allowing students to study directly with master artists without the expense of travel or the limitations of local workshops. What began as a practical response to a challenging economic moment quickly evolved into a platform dedicated to expanding access to high-level representational art education worldwide.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-6">
                                The name "Zarolla" reflects the artistic lineage that shaped my work. It is a combination of the names <strong className="text-accent">Anders Zorn</strong>, <strong className="text-accent">John Singer Sargent</strong>, and <strong className="text-accent">Joaquín Sorolla</strong> — three towering figures I often refer to as "The Usual Suspects." Their influence runs through generations of representational painters, and it is nearly impossible to find a serious realist today who has not been shaped, in some way, by their example. Zarolla<span className="text-[0.7em] align-super">™</span> was built on that same commitment to craftsmanship, observation, and artistic tradition.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-6">
                                Today, the technological landscape looks very different. With a smartphone and an online platform, artists can now broadcast workshops from virtually anywhere in the world — something that was far from possible when Zarolla began. What once required specialized equipment, significant bandwidth, and a willingness to experiment has now become commonplace.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-6">
                                Because of that evolution, Zarolla now serves another important purpose: it stands as a library of a pivotal moment in art education — a recorded archive from the early frontier of digital instruction. While some of the footage reflects the limitations of the era and may appear less polished by today's production standards, the teaching itself remains rigorous, relevant, and deeply grounded in the traditions of representational painting.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-6">
                                Among its most significant offerings is what is believed to be the only recorded class of the late, great <strong className="text-accent">Glen Orbik</strong>. Preserving his instruction was never about production perfection; it was about safeguarding the voice, knowledge, and methodology of a remarkable artist whose influence continues to shape painters today.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-6">
                                From the beginning, my goal has been to create an immersive educational experience for artists who might otherwise never have access to serious training. Talent is universal — opportunity is not. Zarolla was designed to help close that gap while honoring the enduring principles that define great painting.
                            </p>

                            <p className="text-muted-foreground leading-relaxed mb-8">
                                What once felt experimental is now recognized as the future of art education. In many ways, that future began with Zarolla — and today, it remains both a resource for learning and a historical record of master artists teaching at a moment when the digital classroom was just coming into being.
                            </p>

                            {/* Signature */}
                            <div className="border-t pt-6 mt-8">
                                <p className="font-serif text-xl text-foreground italic">
                                    -Tony Pro
                                </p>
                            </div>
                        </div>
                    </Card>

                    {/* CTA */}
                    <div className="mt-16 text-center">
                        <p className="text-muted-foreground mb-6">
                            Ready to begin your journey in representational art?
                        </p>
                        <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
                            <Link to="/courses">
                                Explore Our Courses
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MissionPage;
